<template>
  <div class="container-fluid py-3">
    <div class="row mb-3">
      <div class="col-3">
        <form @submit.prevent="onConnectLedger">
          <template v-if="devices.length > 1">
            <label>Ledger Device:</label>
            <select class="form-control mb-1" v-model="dropdownDevice" @change="onSwitchDevice">
              <option v-for="d in devices" :key="d" :value="d">{{ labelForDevice(d) }}</option>
            </select>
          </template>
          <template v-if="groups.length">
            <label>HD Account:</label>
            <select class="form-control mb-1" v-model.number="dropdownGroup" @change="onSwitchGroup">
              <option v-for="g in groups" :key="g" :value="g">Account #{{ g }}</option>
              <option :value="nextNewGroup">+ New HD account (#{{ nextNewGroup }})</option>
            </select>

            <label>Address:</label>
            <select class="form-control mb-1" v-model.number="dropdownAddress" @change="onSwitchAccount">
              <option v-for="a in addressesInGroup" :key="a.id" :value="a.addressIndex">
                #{{ a.addressIndex }} — {{ a.address.slice(0, 8) }}…{{ a.address.slice(-6) }}
              </option>
              <option :value="nextNewAddressIndex">+ New address (#{{ nextNewAddressIndex }})</option>
            </select>
          </template>
          <label>HD Account / Address Index: (m/84'/0'/x'/0/y)</label>
          <div class="d-flex">
            <input type="number" min="0" class="form-control mr-1" v-model.number="accountGroup" placeholder="account (x)" />
            <input type="number" min="0" class="form-control" v-model.number="addressIndex" placeholder="address (y)" />
          </div>
          <button type="submit" class="btn btn-btc mt-1 w-100" :disabled="connecting">
            {{ connecting ? 'Connecting…' : 'Load from Ledger' }}
          </button>
        </form>
      </div>
      <div class="col-4">
        <label>Address (SegWit bc1…):</label>
        <p class="bold text-break">{{ btcAddress }}</p>
      </div>
      <div class="col-3">
        <label>HD Path:</label>
        <p class="bold">{{ hdPath }}</p>
      </div>
      <div class="col-2">
        <button class="btn btn-secondary btn-sm mt-3 mr-1" @click="logout">Logout</button>
        <button class="btn btn-outline-secondary btn-sm mt-2 w-100" @click="showReceive = true" v-if="btcAddress !== 'N/A'">📥 Receive</button>
      </div>
    </div>

    <!-- Address matrix / tree: HD account groups x address indices, balance per address -->
    <div class="row mb-3" v-if="groups.length">
      <div class="col-12">
        <div class="d-flex align-items-center mb-2">
          <h6 class="mb-0 mr-3">Address Overview</h6>
          <div class="btn-group btn-group-sm mr-2">
            <button class="btn btn-outline-secondary" :class="{ active: matrixView === 'matrix' }" @click="matrixView = 'matrix'">Matrix</button>
            <button class="btn btn-outline-secondary" :class="{ active: matrixView === 'tree' }" @click="matrixView = 'tree'">Tree</button>
          </div>
          <button class="btn btn-sm btn-outline-secondary mr-2" @click="loadMatrix(true)" :disabled="matrixLoading">
            <span v-if="matrixLoading" class="spinner-border spinner-border-sm mr-1" role="status"></span>
            {{ matrixLoading ? 'Loading…' : '↺ Refresh' }}
          </button>
          <span class="text-muted small" v-if="matrixView === 'matrix'">Total ({{ labelForDevice(dropdownDevice) }}): {{ matrixTotalBtc.toFixed(8) }} BTC ({{ priceOf('BTC', matrixTotalBtc) }})</span>
          <span class="text-muted small" v-else>All devices total: {{ treeTotalBtc.toFixed(8) }} BTC ({{ priceOf('BTC', treeTotalBtc) }})</span>
        </div>

        <div class="table-responsive" v-if="matrixView === 'matrix'">
          <table class="table table-sm table-bordered btc-matrix">
            <thead>
              <tr>
                <th>Account \ Address</th>
                <th v-for="col in matrixColumns" :key="col">#{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="g in groups" :key="g">
                <th>#{{ g }}</th>
                <td v-for="col in matrixColumns" :key="col">
                  <div
                    v-if="matrixCells[`${dropdownDevice}:${g}:${col}`]"
                    class="matrix-cell"
                    :class="{ active: accountGroup === g && addressIndex === col }"
                    @click="selectFromMatrixCell(dropdownDevice, g, col)"
                  >
                    <div class="matrix-addr">{{ matrixCells[`${dropdownDevice}:${g}:${col}`].address.slice(0, 6) }}…{{ matrixCells[`${dropdownDevice}:${g}:${col}`].address.slice(-4) }}</div>
                    <div class="matrix-bal">
                      <span v-if="matrixCells[`${dropdownDevice}:${g}:${col}`].loading">…</span>
                      <span v-else>{{ (matrixCells[`${dropdownDevice}:${g}:${col}`].balance || 0).toFixed(8) }} BTC</span>
                    </div>
                  </div>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <AddressTree v-else :data="treeData" @select="onTreeSelect" accent-color="#F7931A" unit="BTC" />
      </div>
    </div>

    <!-- Balance -->
    <div class="row">
      <div class="col-sm-7">
        <div class="total-usdt-banner" v-if="btcAddress !== 'N/A' && btcBalance !== null">
          <span class="tub-label">Total Value</span>
          <span class="tub-value">{{ priceOf('BTC', btcBalance) }} USDT</span>
          <button class="tub-refresh" @click="loadBalance" :disabled="loadingBalance" title="Refresh USDT value">
            <span v-if="loadingBalance" class="spinner-border spinner-border-sm" role="status"></span>
            <span v-else>↻</span>
          </button>
        </div>
        <table class="table table-striped">
          <thead><tr><th>Asset</th><th>Balance</th><th>Value (USDT)</th><th>Info</th></tr></thead>
          <tbody>
            <tr v-if="btcAddress === 'N/A'">
              <td colspan="4" class="text-center text-muted">Connect Ledger to load balance</td>
            </tr>
            <tr v-else>
              <td><span class="ticker-badge">BTC</span></td>
              <td>{{ btcBalance !== null ? btcBalance + ' BTC' : loadingBalance ? 'Loading…' : '—' }}</td>
              <td>{{ priceOf('BTC', btcBalance) }}</td>
              <td><small class="text-muted">Send coming in v2 (UTXO signing)</small></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="col-sm-5">
        <div class="card">
          <div class="card-header small font-weight-bold" style="background:#F7931A;color:#fff">Bitcoin Info</div>
          <div class="card-body small">
            <p class="mb-1">Network: <strong>Bitcoin Mainnet</strong></p>
            <p class="mb-1">Path format: <strong>m/84'/0'/x'/0/0</strong> (Native SegWit)</p>
            <p class="mb-1">Address type: <strong>bech32 (bc1…)</strong></p>
            <p class="mb-0 text-muted">Balance via Blockstream.info API</p>
          </div>
        </div>
      </div>
    </div>

    <!-- TX history -->
    <div class="row mt-3" v-if="recentTxs.length">
      <div class="col-12">
        <h6>Recent Transactions</h6>
        <table class="table table-sm table-striped">
          <thead><tr><th>Status</th><th>Type</th><th>Amount (BTC)</th><th>Explorer</th></tr></thead>
          <tbody>
            <tr v-for="tx in recentTxs" :key="tx.hash">
              <td><span class="badge" :class="tx.status === 'confirmed' ? 'badge-success' : 'badge-warning'">{{ tx.status }}</span></td>
              <td><span class="badge" :class="tx.type === 'IN' ? 'badge-success' : 'badge-primary'">{{ tx.type }}</span></td>
              <td>{{ tx.amount.toFixed(8) }}</td>
              <td><a :href="tx.explorerUrl" target="_blank" class="btn btn-xs btn-outline-secondary" style="font-size:0.7rem;padding:1px 6px">↗</a></td>
            </tr>
          </tbody>
        </table>
        <router-link to="/bitcoin/transactions" class="btn btn-sm btn-outline-secondary">View All Transactions →</router-link>
      </div>
    </div>

    <ReceiveModal v-if="showReceive" :address="btcAddress" chain="bitcoin" @close="showReceive = false" />
  </div>
</template>

<script>
import TransportWebHID from '@ledgerhq/hw-transport-webhid';
import TransportWebUSB from '@ledgerhq/hw-transport-webusb';
import AppBtc from '@ledgerhq/hw-app-btc';
import { fetchPrices } from '../services/priceService';
import { saveAccount, getAccounts } from '../services/accountService';
import { fetchBtcTxs } from '../services/txService';
import { deriveAddress, parseXpub, isValidHex } from '../services/btcXpub';
import ReceiveModal from './ReceiveModal.vue';
import AddressTree from './AddressTree.vue';
import eventBus from '../services/eventBus';

let transport = null;
const XPUB_KEY = 'hd_btc_account_nodes';
const DEVICE_LABELS_KEY = 'hd_btc_device_labels';
const MATRIX_ADDRESS_COUNT = 5;
const XPUB_VERSION_MAINNET = 0x0488b21e;

// Some Ledger firmwares reject a guessed xpubVersion; the error names the
// device's actual version, so retry once with that instead of hardcoding it.
async function fetchAccountXpub(btcApp, accountPath) {
  let version = XPUB_VERSION_MAINNET;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const xpub = await btcApp.getWalletXpub({ path: accountPath, xpubVersion: version });
      const { parentFingerprintHex, pubkeyHex, chainCodeHex } = parseXpub(xpub);
      return { fingerprint: parentFingerprintHex, pubkeyHex, chainCodeHex };
    } catch (e) {
      const m = /xpub version from the device (\d+)/.exec(e.message || '');
      if (m && attempt === 0) { version = Number(m[1]); continue; }
      throw e;
    }
  }
}

function hashKey(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export default {
  name: 'BtcWallet',
  components: { ReceiveModal, AddressTree },
  data() {
    return {
      btcAddress: 'N/A',
      deviceFingerprint: '',
      accountGroup: 0,
      addressIndex: 0,
      hdPath: 'N/A',
      btcBalance: null,
      loadingBalance: false,
      connecting: false,
      showReceive: false,
      prices: {},
      priceTimer: null,
      recentTxs: [],
      savedAccounts: [],
      deviceLabels: {},
      dropdownDevice: '',
      dropdownGroup: 0,
      dropdownAddress: 0,
      accountNodes: {},
      matrixCells: {},
      matrixLoading: false,
      matrixView: 'matrix',
    };
  },
  computed: {
    devices() {
      const known = new Set(this.savedAccounts.map(a => a.deviceFingerprint).filter(Boolean));
      Object.keys(this.accountNodes).forEach(k => known.add(k.split(':')[0]));
      return [...known].sort((a, b) => (this.deviceLabels[a] || 0) - (this.deviceLabels[b] || 0));
    },
    groups() {
      const known = new Set(
        this.savedAccounts.filter(a => a.deviceFingerprint === this.dropdownDevice).map(a => a.accountGroup)
      );
      Object.keys(this.accountNodes).forEach(k => {
        const [fp, g] = k.split(':');
        if (fp === this.dropdownDevice) known.add(Number(g));
      });
      return [...known].sort((a, b) => a - b);
    },
    nextNewGroup() {
      return this.groups.reduce((max, g) => Math.max(max, g), -1) + 1;
    },
    addressesInGroup() {
      return this.savedAccounts
        .filter(a => a.deviceFingerprint === this.dropdownDevice && a.accountGroup === this.dropdownGroup)
        .sort((a, b) => a.addressIndex - b.addressIndex);
    },
    nextNewAddressIndex() {
      return this.addressesInGroup.reduce((max, a) => Math.max(max, a.addressIndex), -1) + 1;
    },
    matrixColumns() {
      const maxKnown = this.savedAccounts
        .filter(a => a.deviceFingerprint === this.dropdownDevice)
        .reduce((max, a) => Math.max(max, a.addressIndex), -1);
      const count = Math.max(MATRIX_ADDRESS_COUNT, maxKnown + 1);
      return Array.from({ length: count }, (_, i) => i);
    },
    matrixTotalBtc() {
      const prefix = `${this.dropdownDevice}:`;
      return Object.entries(this.matrixCells)
        .filter(([k]) => k.startsWith(prefix))
        .reduce((s, [, c]) => s + (c.balance || 0), 0);
    },
    treeTotalBtc() {
      return Object.values(this.matrixCells).reduce((s, c) => s + (c.balance || 0), 0);
    },
    treeData() {
      const root = { name: 'Ledger Wallets', children: [] };
      for (const fp of this.devices) {
        const deviceNode = { name: this.labelForDevice(fp), fingerprint: fp, children: [] };
        for (const group of this.groupsForDevice(fp)) {
          const groupNode = { name: `Account #${group}`, fingerprint: fp, group, children: [] };
          for (const idx of this.matrixColumns) {
            const cell = this.matrixCells[`${fp}:${group}:${idx}`];
            if (!cell) continue;
            groupNode.children.push({
              name: `#${idx}`,
              fingerprint: fp,
              group,
              addressIndex: idx,
              address: cell.address,
              balance: cell.balance,
              loading: cell.loading,
              isActive: fp === this.deviceFingerprint && group === this.accountGroup && idx === this.addressIndex,
            });
          }
          if (groupNode.children.length) deviceNode.children.push(groupNode);
        }
        if (deviceNode.children.length) root.children.push(deviceNode);
      }
      return root;
    },
  },
  methods: {
    refreshSavedAccounts() {
      this.savedAccounts = getAccounts()
        .filter(a => a.chain === 'bitcoin' && a.accountGroup !== undefined && a.addressIndex !== undefined && a.deviceFingerprint);
      if (this.savedAccounts.some(
        a => a.deviceFingerprint === this.deviceFingerprint && a.accountGroup === this.accountGroup && a.addressIndex === this.addressIndex
      )) {
        this.dropdownDevice = this.deviceFingerprint;
        this.dropdownGroup = this.accountGroup;
        this.dropdownAddress = this.addressIndex;
      }
    },
    loadAccountNodes() {
      try {
        this.accountNodes = JSON.parse(localStorage.getItem(XPUB_KEY) || '{}');
      } catch {
        this.accountNodes = {};
      }
      try {
        this.deviceLabels = JSON.parse(localStorage.getItem(DEVICE_LABELS_KEY) || '{}');
      } catch {
        this.deviceLabels = {};
      }
    },
    ensureDeviceLabel(fingerprint) {
      if (!(fingerprint in this.deviceLabels)) {
        const nextIdx = Object.keys(this.deviceLabels).length + 1;
        this.deviceLabels = { ...this.deviceLabels, [fingerprint]: nextIdx };
        localStorage.setItem(DEVICE_LABELS_KEY, JSON.stringify(this.deviceLabels));
      }
      return this.deviceLabels[fingerprint];
    },
    labelForDevice(fingerprint) {
      const idx = this.deviceLabels[fingerprint];
      return idx ? `Ledger #${idx} (${fingerprint})` : fingerprint;
    },
    saveAccountNode(fingerprint, group, pubkeyHex, chainCodeHex) {
      if (!isValidHex(pubkeyHex) || !isValidHex(chainCodeHex)) {
        console.error('[BTC matrix] refusing to cache non-hex account node', { fingerprint, group, pubkeyHex, chainCodeHex });
        this.notice('error', 'Address Matrix Unavailable', 'Ledger returned malformed key data — could not cache this account for the matrix.', 8);
        return;
      }
      const key = `${fingerprint}:${group}`;
      this.accountNodes = { ...this.accountNodes, [key]: { pubkeyHex, chainCodeHex } };
      localStorage.setItem(XPUB_KEY, JSON.stringify(this.accountNodes));
      this.ensureDeviceLabel(fingerprint);
      this.loadMatrix();
    },
    groupsForDevice(fp) {
      const known = new Set(this.savedAccounts.filter(a => a.deviceFingerprint === fp).map(a => a.accountGroup));
      Object.keys(this.accountNodes).forEach(k => {
        const [kfp, g] = k.split(':');
        if (kfp === fp) known.add(Number(g));
      });
      return [...known].sort((a, b) => a - b);
    },
    async loadMatrix(notifyIfEmpty = false) {
      console.log('[BTC matrix] devices:', this.devices, 'accountNodes keys:', Object.keys(this.accountNodes), 'savedAccounts:', this.savedAccounts);
      const cells = {};
      const toFetch = [];
      for (const fp of this.devices) {
        const groupsHere = this.groupsForDevice(fp);
        console.log(`[BTC matrix] device ${fp} groups:`, groupsHere);
        for (const group of groupsHere) {
          let node = this.accountNodes[`${fp}:${group}`];
          console.log(`[BTC matrix] device ${fp} group ${group} has cached xpub node:`, !!node);
          if (node && (!isValidHex(node.pubkeyHex) || !isValidHex(node.chainCodeHex))) {
            console.warn(`[BTC matrix] cached xpub for ${fp}:${group} is corrupt (pre-fix cache) — ignoring, reconnect to regenerate`);
            node = null;
          }
          for (const idx of this.matrixColumns) {
            const key = `${fp}:${group}:${idx}`;
            let address = null;
            if (node) {
              try {
                address = deriveAddress(node.pubkeyHex, node.chainCodeHex, idx);
              } catch (e) {
                console.error(`Failed to derive BTC address for account ${group}/${idx}:`, e);
              }
            } else {
              const saved = this.savedAccounts.find(
                a => a.deviceFingerprint === fp && a.accountGroup === group && a.addressIndex === idx
              );
              if (saved) address = saved.address;
            }
            if (address) {
              cells[key] = { address, balance: null, loading: true };
              toFetch.push({ key, address });
            }
          }
        }
      }
      this.matrixCells = cells;
      console.log(`[BTC matrix] built ${toFetch.length} address cell(s) to fetch balances for`);
      if (!toFetch.length) {
        if (notifyIfEmpty) {
          this.notice('error', 'Address Matrix Empty', 'No cached addresses/xpubs found — reconnect your Ledger account(s) to populate it.', 8);
        }
        return;
      }
      this.matrixLoading = true;
      const results = await Promise.allSettled(
        toFetch.map(async ({ key, address }) => ({ key, ...(await this.fetchAddressBalance(address)) }))
      );
      const updated = { ...this.matrixCells };
      for (const r of results) {
        if (r.status === 'fulfilled' && updated[r.value.key]) {
          updated[r.value.key] = { ...updated[r.value.key], balance: r.value.balance, loading: false };
        } else if (r.status === 'rejected') {
          console.error('[BTC matrix] balance fetch rejected:', r.reason);
        }
      }
      this.matrixCells = updated;
      this.matrixLoading = false;
    },
    selectFromMatrixCell(fp, group, addressIndex) {
      const cell = this.matrixCells[`${fp}:${group}:${addressIndex}`];
      if (!cell) return;
      this.deviceFingerprint = fp;
      this.accountGroup = group;
      this.addressIndex = addressIndex;
      this.dropdownDevice = fp;
      this.dropdownGroup = group;
      this.dropdownAddress = addressIndex;
      this.btcAddress = cell.address;
      this.hdPath = `m/84'/0'/${group}'/0/${addressIndex}`;
      this.loadBalance();
      this.loadRecentTxs();
      this.refreshPrices();
    },
    onTreeSelect({ fingerprint, group, addressIndex }) {
      this.selectFromMatrixCell(fingerprint, group, addressIndex);
    },
    onSwitchGroup() {
      this.accountGroup = this.dropdownGroup;
      const first = this.addressesInGroup[0];
      this.dropdownAddress = first ? first.addressIndex : 0;
      this.addressIndex = this.dropdownAddress;
      this.onConnectLedger();
    },
    onSwitchAccount() {
      this.accountGroup = this.dropdownGroup;
      this.addressIndex = this.dropdownAddress;
      this.onConnectLedger();
    },
    onSwitchDevice() {
      const first = this.groups[0];
      this.dropdownGroup = first !== undefined ? first : 0;
      const firstAddr = this.addressesInGroup[0];
      this.dropdownAddress = firstAddr ? firstAddr.addressIndex : 0;
      this.loadMatrix();
      this.notice('info', 'Switch Ledger', 'Plug in / select that Ledger, then reconnect to verify it.', 5);
    },
    async createTransport() {
      if (await TransportWebHID.isSupported()) return TransportWebHID.create();
      if (await TransportWebUSB.isSupported()) return TransportWebUSB.create();
      throw new Error('WebHID/WebUSB not supported. Please use Chrome or Edge over HTTPS.');
    },

    async onConnectLedger(e) {
      if (e) e.preventDefault();
      this.connecting = true;
      this.notice('info', 'Connecting', 'Opening Ledger connection…', 3);
      try {
        if (transport) await transport.close().catch(() => {});
        transport = await this.createTransport();
        const btcApp = new AppBtc({ transport });

        const path = `84'/0'/${this.accountGroup}'/0/${this.addressIndex}`;
        this.hdPath = 'm/' + path;

        const pubKey = await btcApp.getWalletPublicKey(path, {
          verify: false,
          format: 'bech32',
        });
        this.btcAddress = pubKey.bitcoinAddress;
        this.notice('success', 'Connected', 'BTC address loaded.');

        try {
          const { fingerprint, pubkeyHex, chainCodeHex } = await fetchAccountXpub(btcApp, `84'/0'/${this.accountGroup}'`);
          if (this.dropdownDevice && this.dropdownDevice !== fingerprint) {
            this.notice('error', 'Wrong Ledger', `Expected device ${this.dropdownDevice} but got ${fingerprint} — is a different Ledger plugged in?`, 8);
          }
          this.deviceFingerprint = fingerprint;
          this.dropdownDevice = fingerprint;
          this.saveAccountNode(fingerprint, this.accountGroup, pubkeyHex, chainCodeHex);
        } catch (e) {
          console.error('Failed to fetch account xpub for matrix view:', e);
          this.notice('error', 'Address Matrix Unavailable', 'Could not fetch account xpub: ' + (e.message || e), 10);
        }

        await this.loadBalance();
        await this.loadRecentTxs();
        await this.refreshPrices();

        const accounts = saveAccount({
          chain: 'bitcoin',
          hdIndex: hashKey(`${this.deviceFingerprint}-${this.accountGroup}-${this.addressIndex}`),
          deviceFingerprint: this.deviceFingerprint,
          accountGroup: this.accountGroup,
          addressIndex: this.addressIndex,
          address: this.btcAddress,
          label: `Bitcoin ${this.labelForDevice(this.deviceFingerprint)} #${this.accountGroup}.${this.addressIndex}`,
        });
        this.refreshSavedAccounts();
        this.loadMatrix();
        if (this.$store) {
          this.$store.commit('accounts/SET_ACCOUNTS', accounts);
          this.$store.commit('wallets/SET_WALLET', {
            chain: 'bitcoin',
            data: { address: this.btcAddress, balances: [{ ticker: 'BTC', balance: String(this.btcBalance || 0), rawBalance: this.btcBalance || 0, connected: true }], connected: true, accountIndex: this.accountGroup },
          });
        }
      } catch (err) {
        this.notice('error', 'Ledger Error', err.message || String(err), 10);
        console.error(err);
      } finally {
        this.connecting = false;
      }
    },

    async fetchAddressBalance(address) {
      const apis = [
        `https://blockstream.info/api/address/${address}`,
        `https://mempool.space/api/address/${address}`,
      ];
      const errors = [];
      for (const url of apis) {
        try {
          const res = await fetch(url);
          const text = await res.text();
          if (!res.ok) {
            errors.push(`${url} -> HTTP ${res.status}: ${text.slice(0, 200)}`);
            continue;
          }
          const data = JSON.parse(text);
          console.log(`[BTC balance] fetched from ${url}`, data);
          const funded = (data.chain_stats && data.chain_stats.funded_txo_sum) || 0;
          const spent  = (data.chain_stats && data.chain_stats.spent_txo_sum)  || 0;
          return { balance: (funded - spent) / 1e8, error: null };
        } catch (e) {
          errors.push(`${url} -> ${e.message || e}`);
        }
      }
      console.error('[BTC balance] all sources failed:\n' + errors.join('\n'));
      return { balance: null, error: errors.join(' | ') };
    },

    async loadBalance() {
      if (!this.btcAddress || this.btcAddress === 'N/A') {
        this.notice('error', 'Balance Error', 'No BTC address loaded from Ledger — try reconnecting.', 5);
        return;
      }
      this.loadingBalance = true;
      const { balance, error } = await this.fetchAddressBalance(this.btcAddress);
      if (balance !== null) {
        this.btcBalance = balance.toFixed(8);
      } else {
        this.notice('error', 'Balance Error', 'Could not fetch balance: ' + error, 8);
      }
      this.loadingBalance = false;
    },

    async loadRecentTxs() {
      try {
        const txs = await fetchBtcTxs(this.btcAddress);
        this.recentTxs = txs.slice(0, 5);
      } catch {}
    },

    async refreshPrices() {
      const { prices } = await fetchPrices(['BTC']);
      this.prices = prices;
      if (this.$store) this.$store.dispatch('prices/fetchPrices', ['BTC']);
    },

    priceOf(ticker, rawBalance) {
      if (rawBalance === null || rawBalance === undefined) return '—';
      const raw = parseFloat(String(rawBalance).replace(/,/g, '')) || 0;
      const price = this.prices[ticker] ?? null;
      if (price === null) return '—';
      return '$' + (raw * price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    logout() { localStorage.removeItem('isLoggedIn'); window.location.reload(); },

    notice(type, title, description, duration) {
      if (this.$notice) this.$notice[type]({ title, description: description || '', duration: duration ?? 1 });
    },
  },
  mounted() {
    this.priceTimer = setInterval(() => this.refreshPrices(), 60000);
    eventBus.$on('refresh-prices', this.refreshPrices);
    this.loadAccountNodes();
    this.refreshSavedAccounts();
    const active = localStorage.getItem('hd_active_bitcoin');
    if (active) {
      const accts = JSON.parse(localStorage.getItem('hd_accounts') || '[]');
      const a = accts.find(x => x.id === active);
      if (a && a.accountGroup !== undefined && a.addressIndex !== undefined && a.deviceFingerprint) {
        this.deviceFingerprint = a.deviceFingerprint;
        this.accountGroup = a.accountGroup;
        this.addressIndex = a.addressIndex;
        this.dropdownDevice = a.deviceFingerprint;
        this.dropdownGroup = a.accountGroup;
        this.dropdownAddress = a.addressIndex;
      }
    }
    if (!this.dropdownDevice && this.devices.length) {
      this.dropdownDevice = this.devices[0];
    }
    this.refreshPrices();
    this.loadMatrix();
  },
  beforeDestroy() { clearInterval(this.priceTimer); eventBus.$off('refresh-prices', this.refreshPrices); },
};
</script>

<style scoped>
.btn-btc { background-color: #F7931A; color: #fff; border: none; font-weight: 600; }
.btn-btc:hover { background-color: #d9790f; color: #fff; }
.table thead th { background-color: #F7931A; color: #fff; font-weight: 700; border: none; }
.ticker-badge { display: inline-block; background: #F7931A; color: #fff; font-size: 0.75rem; font-weight: 700; padding: 2px 8px; border-radius: 10px; }
.btc-matrix td, .btc-matrix th { vertical-align: middle; text-align: center; font-size: 0.8rem; }
.matrix-cell { cursor: pointer; padding: 4px; border-radius: 4px; }
.matrix-cell:hover { background: rgba(247, 147, 26, 0.15); }
.matrix-cell.active { background: rgba(247, 147, 26, 0.3); font-weight: 600; }
.matrix-addr { font-family: monospace; font-size: 0.75rem; }
.matrix-bal { font-size: 0.75rem; color: #666; }
</style>
